import type { BandStatus, GradingStatus, TestResult } from '@/lib/types'

export interface GradingPresentation {
  label: string
  tone: 'positive' | 'pending' | 'warning' | 'danger' | 'neutral'
  terminal: boolean
}

export function resolveGradingStatus(result: Pick<TestResult, 'grading_status' | 'status'>): GradingStatus {
  if (result.status === 'abandoned') return 'unassessable'
  if (result.grading_status === 'completed') return 'succeeded'
  if (result.grading_status) return result.grading_status
  return 'pending'
}

export function gradingPresentation(status: GradingStatus): GradingPresentation {
  switch (status) {
    case 'succeeded':
      return { label: 'Ready to review', tone: 'positive', terminal: true }
    case 'failed':
      return { label: 'Assessment failed', tone: 'danger', terminal: true }
    case 'unassessable':
      return { label: 'Not assessable', tone: 'warning', terminal: true }
    case 'needs_review':
      return { label: 'Human review', tone: 'warning', terminal: false }
    case 'running':
      return { label: 'Assessing', tone: 'pending', terminal: false }
    default:
      return { label: 'Queued', tone: 'pending', terminal: false }
  }
}

export function resultPresentation(result: Pick<TestResult, 'grading_status' | 'status'>): GradingPresentation {
  if (result.status === 'abandoned') {
    return { label: 'Not finished', tone: 'warning', terminal: true }
  }
  return gradingPresentation(resolveGradingStatus(result))
}

export function publishedBand(result: TestResult): number | null {
  if (result.status === 'abandoned') return null
  const status = resolveGradingStatus(result)
  if (status !== 'succeeded' || result.score_publication_status !== 'published') return null
  const band = result.section_scores?.overall_band
  return typeof band === 'number' && Number.isFinite(band) ? band : null
}

/**
 * The state chip shown in place of a band figure on an attempt row
 * (dashboard activity, history, results journal).
 *
 * `gradingPresentation('succeeded')` is "Ready to review" - correct as a
 * lifecycle badge (it is also used, unmodified, for `ResultDetailPage`'s own
 * header), but wrong reused here for a published, succeeded sitting whose
 * overall band is null because no section earned one (IELTS-462): nothing is
 * pending review on that row, so "Ready to review" reads as an open task that
 * does not exist. This is the one place that distinction matters, so it is a
 * dedicated function rather than a change to `gradingPresentation` itself.
 */
export function bandRowLabel(result: TestResult): GradingPresentation {
  const presentation = resultPresentation(result)
  if (resolveGradingStatus(result) === 'succeeded' && result.score_publication_status === 'published' && publishedBand(result) === null) {
    return { label: 'No band issued', tone: 'neutral', terminal: true }
  }
  return presentation
}

/**
 * IELTS-89: the Indicative / Confirmed tag for a published band.
 *
 * Owner decision 2026-09-26: the AI band shows at once, labelled Indicative,
 * and reads Confirmed once an examiner approves it. The label is the server's
 * `band_status` (Law 4) - read here, never derived. `published` and a missing
 * field (an API older than this app) return null, so every surface renders
 * exactly as it did before.
 */
export function bandStatusPresentation(status: BandStatus | null | undefined): GradingPresentation | null {
  if (status === 'indicative') return { label: 'Indicative', tone: 'warning', terminal: true }
  if (status === 'confirmed') return { label: 'Confirmed', tone: 'positive', terminal: true }
  return null
}

/** The tag for an attempt row, only ever beside a band the row actually shows. */
export function rowBandStatus(result: TestResult): GradingPresentation | null {
  if (publishedBand(result) === null) return null
  return bandStatusPresentation(result.band_status)
}
